import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { asset } from "@/lib/asset";

const HeroSection = () => {
  const { t } = useTranslation();

  const stats = [
    { value: t("home.hero.stat1.value"), label: t("home.hero.stat1.label") },
    { value: t("home.hero.stat2.value"), label: t("home.hero.stat2.label") },
    { value: t("home.hero.stat3.value"), label: t("home.hero.stat3.label") },
    { value: t("home.hero.stat4.value"), label: t("home.hero.stat4.label") },
  ];

  return (
    <section className="relative min-h-[90vh] md:min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={asset("/editorial/home-hero.jpg")}
          alt="Forwaro Furniture Showroom"
          className="w-full h-full object-cover object-top"
        />
        {/* Mobile: stronger bottom-up gradient for readability.
            Desktop: left-to-right for hero-beside-product layout. */}
        <div className="absolute inset-0 md:hidden bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
        <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
      </div>

      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 pt-24 pb-20 md:pb-16">
        <div className="max-w-2xl">
          {/* Badge — subtle uppercase kicker on all viewports, no pill. */}
          <p className="text-amber-400 text-[11px] md:text-xs font-semibold tracking-[0.2em] uppercase mb-6 md:mb-8">
            {t("home.hero.badge")}
          </p>

          <h1 className="text-[2.5rem] leading-[1.1] md:text-6xl md:leading-tight font-bold text-white mb-5 md:mb-6">
            {t("home.hero.headline.line1")}
            <br />
            <span className="text-amber-400">{t("home.hero.headline.line2Highlight")}</span>
            {t("home.hero.headline.line3") && (
              <>
                <br />
                {t("home.hero.headline.line3")}
              </>
            )}
          </h1>

          <p className="text-white/75 text-base md:text-lg leading-relaxed mb-8 md:mb-10 max-w-xl">
            {t("home.hero.subheadline")}
          </p>

          {/* CTAs — mobile shows only primary, desktop shows both */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Link
              to="/products"
              className="bg-amber-700 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 text-sm text-center whitespace-nowrap cursor-pointer"
            >
              {t("cta.exploreCatalog")}
            </Link>
            <Link
              to="/contact?intent=quote"
              className="hidden sm:inline-block border border-white/40 hover:border-white/80 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 text-sm text-center whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              {t("cta.requestQuote")}
            </Link>
          </div>

          {/* Stats — desktop only. On mobile these repeat in the About section below. */}
          <div className="hidden md:flex flex-wrap gap-8 mt-14 pt-10 border-t border-white/20">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-white text-2xl font-bold">{stat.value}</p>
                <p className="text-white/50 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-8 inset-x-0 flex justify-center pointer-events-none">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-[10px] md:text-xs tracking-widest uppercase">
            {t("home.hero.scroll")}
          </span>
          <i className="ri-arrow-down-line text-white/40 text-lg" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
